import { Link, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";

interface Contribution {
  title: string;
  url: string;
  repository: string;
}

const contributions: Contribution[] = [
  {
    title: "Show seconds (PR #8392)",
    url: "https://github.com/nightscout/cgm-remote-monitor/pull/8392",
    repository: "nightscout/cgm-remote-monitor",
  },
  {
    title: "Wip/clock units (PR #8388)",
    url: "https://github.com/nightscout/cgm-remote-monitor/pull/8388",
    repository: "nightscout/cgm-remote-monitor",
  },
  {
    title: "Fix api3 limit error when limit is string (PR #8422)",
    url: "https://github.com/nightscout/cgm-remote-monitor/pull/8422",
    repository: "nightscout/cgm-remote-monitor",
  },
  {
    title: "Apiv3 (PR #206)",
    url: "https://github.com/timoschlueter/nightscout-librelink-up/pull/206",
    repository: "timoschlueter/nightscout-librelink-up",
  },
  {
    title:
      "Suggestion: export settings from maintenance to downloads/Google Drive (Issue #3296 comment)",
    url: "https://github.com/nightscout/AndroidAPS/issues/3296#issuecomment-2263255698",
    repository: "nightscout/AndroidAPS",
  },
  {
    title:
      "Feature Request: Improve glucose balancing under extreme weather conditions (Issue #3980 comment)",
    url: "https://github.com/nightscout/AndroidAPS/issues/3980#issuecomment-2871912801",
    repository: "nightscout/AndroidAPS",
  },
  {
    title:
      "[github actions] Add a simple and easy-to-use CI build process (PR #3921 comment)",
    url: "https://github.com/nightscout/AndroidAPS/pull/3921#issuecomment-2855039113",
    repository: "nightscout/AndroidAPS",
  },
  {
    title:
      "Add command line method for transferring wear apk to smartwatch (PR #2500 event)",
    url: "https://github.com/openaps/AndroidAPSdocs/pull/2500#event-23585988566",
    repository: "openaps/AndroidAPSdocs",
  },
  {
    title: "Update Building-APK.md (PR #1524)",
    url: "https://github.com/openaps/AndroidAPSdocs/pull/1524",
    repository: "openaps/AndroidAPSdocs",
  },
  {
    title: "Add caresens instructions (PR #2545)",
    url: "https://github.com/openaps/AndroidAPSdocs/pull/2545",
    repository: "openaps/AndroidAPSdocs",
  },
  {
    title: "Update autotune.md (PR #1587)",
    url: "https://github.com/openaps/docs/pull/1587",
    repository: "openaps/docs",
  },
  {
    title: "change position of switch argument (PR #1579)",
    url: "https://github.com/openaps/docs/pull/1579",
    repository: "openaps/docs",
  },
  {
    title:
      "Empty dialogue when logging CGM change via Action tab (minor cosmetics) (Issue #3265)",
    url: "https://github.com/nightscout/AndroidAPS/issues/3265",
    repository: "nightscout/AndroidAPS",
  },
  {
    title:
      "Feature request for WearOS: TIR indicator light/color/gauge (Issue #2271)",
    url: "https://github.com/nightscout/AndroidAPS/issues/2271",
    repository: "nightscout/AndroidAPS",
  },
  {
    title: "[3.0-beta6] Bolus incerements discrepancy (Issue #946)",
    url: "https://github.com/nightscout/AndroidAPS/issues/946",
    repository: "nightscout/AndroidAPS",
  },
];

const Page = () => {
  const { t } = useTranslation();
  usePageView();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Seo title="Contributions" />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            pt: 10,
            mt: 2,
            mb: 4,
            color: theme.palette.primary.main,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                textAlign: "center",
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  mt: 2,
                  mb: 2,
                  color: theme.palette.primary.main,
                }}
              >
                {t("Our Contributions")}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", color: "text.secondary", mb: 5 }}
              >
                {t(
                  "Contributions from our team to the open-source diabetes software eco-system."
                )}
              </Typography>
            </Box>
          </Container>
        </Box>

        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            pb: smDown ? 6 : 10,
          }}
        >
          <Container maxWidth="lg">
            <Card
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                border: "1px solid #F3F4F6",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {contributions.map((contribution) => (
                    <Box
                      component="li"
                      key={contribution.url}
                      sx={{
                        mb: 2,
                        "&:last-child": { mb: 0 },
                      }}
                    >
                      <Link
                        href={contribution.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          lineHeight: 1.6,
                        }}
                      >
                        {contribution.title}
                      </Link>
                      <Typography variant="body2" color="text.secondary">
                        {contribution.repository}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Page;
